import { create } from 'zustand';
import type { SessionUserProfileDetailedResponse } from '@pillar-portal/api-types';

export type EffectiveSessionGeneration = {
    readonly value: number;
};

const INITIAL_EFFECTIVE_SESSION_GENERATION: EffectiveSessionGeneration = Object.freeze({
    value: 0,
});

function nextEffectiveSessionGeneration(
    generation: EffectiveSessionGeneration
): EffectiveSessionGeneration {
    return Object.freeze({ value: generation.value + 1 });
}

interface AuthState {
    user: SessionUserProfileDetailedResponse | null;
    audit_id: number | null;
    enableJavascriptConsole: boolean;
    isAuthenticated: boolean;
    isLoading: boolean;
    activeRoleAssignmentId: number | null;
    effectiveSessionGeneration: EffectiveSessionGeneration;

    // NEW STATE: Flag to stop the profile query loop during logout/redirect
    isLoggingOut: boolean;
    logoutFailure: 'server' | 'native' | null;
    setLogoutFailure: (logoutFailure: AuthState['logoutFailure']) => void;

    setUser: (user: SessionUserProfileDetailedResponse | null) => void;
    setUserForEffectiveSessionGeneration: (
        user: SessionUserProfileDetailedResponse | null,
        generation: EffectiveSessionGeneration
    ) => boolean;
    setLoading: (loading: boolean) => void;
    setEnableJavascriptConsole: (enabled: boolean) => void;
    setActiveRoleAssignmentId: (activeRoleAssignmentId: number | null) => void;
    setActiveRoleAssignmentIdForEffectiveSessionGeneration: (
        activeRoleAssignmentId: number | null,
        generation: EffectiveSessionGeneration
    ) => boolean;
    getEffectiveSessionGeneration: () => EffectiveSessionGeneration;
    isEffectiveSessionGenerationCurrent: (generation: EffectiveSessionGeneration) => boolean;
    advanceEffectiveSessionGeneration: () => EffectiveSessionGeneration;
    beginEffectiveSessionRefresh: () => void;
    logout: () => void;

    // NEW ACTION: Setter for the new state flag
    setIsLoggingOut: (status: boolean) => void;
}

const DEFAULT_CONSOLE_LOGGING_ENABLED: boolean = import.meta.env.DEV;

export const useAuthStore = create<AuthState>((set, get) => ({
    user: null,
    audit_id: null,
    enableJavascriptConsole: DEFAULT_CONSOLE_LOGGING_ENABLED,
    isAuthenticated: false,
    isLoading: true,
    activeRoleAssignmentId: null,
    effectiveSessionGeneration: INITIAL_EFFECTIVE_SESSION_GENERATION,

    // Initialize new state
    isLoggingOut: false,
    logoutFailure: null,
    setLogoutFailure: (logoutFailure) => set({ logoutFailure }),

    setUser: (user) =>
        set({
            user,
            isAuthenticated: !!user,
            isLoading: false,
        }),

    setUserForEffectiveSessionGeneration: (user, generation) => {
        const state = get();
        if (!state.isEffectiveSessionGenerationCurrent(generation)) {
            return false;
        }
        state.setUser(user);
        return true;
    },

    setLoading: (loading) => set({ isLoading: loading }),

    setEnableJavascriptConsole: (enabled) => set({ enableJavascriptConsole: enabled }),

    setActiveRoleAssignmentId: (activeRoleAssignmentId) => set({ activeRoleAssignmentId }),

    setActiveRoleAssignmentIdForEffectiveSessionGeneration: (
        activeRoleAssignmentId,
        generation
    ) => {
        const state = get();
        if (!state.isEffectiveSessionGenerationCurrent(generation)) {
            return false;
        }
        state.setActiveRoleAssignmentId(activeRoleAssignmentId);
        return true;
    },

    getEffectiveSessionGeneration: (): EffectiveSessionGeneration =>
        get().effectiveSessionGeneration,

    isEffectiveSessionGenerationCurrent: (generation) =>
        get().effectiveSessionGeneration.value === generation.value,

    advanceEffectiveSessionGeneration: () => {
        let nextGeneration: EffectiveSessionGeneration = INITIAL_EFFECTIVE_SESSION_GENERATION;
        set((state) => {
            nextGeneration = nextEffectiveSessionGeneration(state.effectiveSessionGeneration);
            return {
                effectiveSessionGeneration: nextGeneration,
            };
        });
        return nextGeneration;
    },

    beginEffectiveSessionRefresh: () =>
        set((state) => {
            return {
                user: null,
                audit_id: null,
                isAuthenticated: true,
                isLoading: true,
                activeRoleAssignmentId: null,
                effectiveSessionGeneration: nextEffectiveSessionGeneration(
                    state.effectiveSessionGeneration
                ),
            };
        }),

    logout: () =>
        set((state) => {
            return {
                user: null,
                audit_id: null,
                isAuthenticated: false,
                isLoading: false,
                enableJavascriptConsole: DEFAULT_CONSOLE_LOGGING_ENABLED,
                activeRoleAssignmentId: null,
                effectiveSessionGeneration: nextEffectiveSessionGeneration(
                    state.effectiveSessionGeneration
                ),
                // NOTE: We do not set isLoggingOut here. The ProtectedRoute
                // component handles setting this flag *before* calling logout()
                // and redirecting to prevent race conditions.
            };
        }),

    // Implement new action
    setIsLoggingOut: (status) => set({ isLoggingOut: status, logoutFailure: null }),
}));
